import Events from './Events';
import AppData from './AppData';
import Lesson from './Lesson';
import Repertoire from './Repertoire';
import TutorialProgress from './TutorialProgress';
import ExerciseProgress from './ExerciseProgress';

import { all, spawn } from 'redux-saga/effects';
import { reportError } from 'Utils/reportError';

/**
 * MUS-540 defense-in-depth for the root saga.
 *
 * Before this, the root saga was a flat `all([...Events, ...AppData, ...])`. In
 * redux-saga, an uncaught error in any effect of a flat `all([...])` propagates
 * to the root and CANCELS EVERY sibling - so a single unguarded fetch that threw
 * (e.g. an enforce-mode 403 in `onAuthStartup`) tore down the whole saga tree and
 * stranded the app. The per-boot fix for the known strand lives in
 * `onAuthStartup` (Sagas/AppData.ts); this is the belt-and-braces layer so that
 * NO single uncaught error, from any future fetch, can take the whole tree down.
 *
 * Two independent hardenings:
 *
 *   1. Each feature module runs inside its own `spawn` - a DETACHED task. An
 *      uncaught error in a detached task does not propagate to its parent, so one
 *      module dying can no longer cancel its siblings; the tree as a whole
 *      survives. (Isolation, not resurrection: a module that dies stays down for
 *      that boot - which is why the real anti-strand fix is in `onAuthStartup`,
 *      keeping AppData's own boot alive. This layer protects the OTHER modules.)
 *   2. `sagaOnError` is wired as the saga middleware's top-level `onError` (see
 *      Reducers/index.ts), so any error that reaches the root - including out of a
 *      detached feature task - is REPORTED to RUM instead of only being
 *      console-logged by redux-saga's default handler.
 */
const defaultFeatureSagas = [
  Events,
  AppData,
  Lesson,
  Repertoire,
  TutorialProgress,
  ExerciseProgress,
];

/**
 * The root saga. `featureSagas` is injectable for tests only; production always
 * uses the real modules via the default. Each module's effect array is run in a
 * detached `spawn` so one module's uncaught failure cannot cancel the others.
 */
export default function* rootSaga(featureSagas: unknown[][] = defaultFeatureSagas) {
  yield all(
    featureSagas.map((effects) =>
      spawn(function* featureRoot() {
        yield all(effects);
      }),
    ),
  );
}

/**
 * Top-level saga error handler. redux-saga invokes this for any error that
 * reaches the root uncaught (with the default `spawn` isolation above, that is a
 * feature module whose watcher set died). We report it to RUM for visibility and
 * return - there is nothing to rethrow at the top of the tree.
 */
export function sagaOnError(error: Error, errorInfo: { sagaStack: string }): void {
  reportError(error, 'saga.root');
  // `sagaStack` is a redux-saga breadcrumb string, not an Error; keep it in the
  // console for local debugging without inflating the RUM record.
  if (errorInfo?.sagaStack) {
    console.error('[saga.root] sagaStack:', errorInfo.sagaStack);
  }
}
